// GameBar.js — the slim bar across the top of the games (game mode, App.js):
// the logo back to the site, the three games as tabs, and buttons for how to
// play (an info icon), your stats and past puzzles. No site menu or footer, so nothing
// competes with the puzzle. Help and stats are opened on the game's own page
// (GamePage) through window events, so the bar needs no game state.

import React from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import CalendarMonthRounded from "@mui/icons-material/CalendarMonthRounded";
import { BRAND, accentTint } from "../../theme";
import { BrandLockup, LogoMark } from "../AppHeader";

export const GAME_ROUTES = ["/daily", "/daily/season", "/higher-or-lower", "/daily/archive"];

const TABS = [
  { to: "/daily", label: "Scorigami Daily", short: "Daily" },
  { to: "/daily/season", label: "Mystery Season", short: "Season" },
  { to: "/higher-or-lower", label: "Higher or Lower", short: "Higher/Lower" },
];

export const openGameHelp = () => window.dispatchEvent(new Event("footygami:help"));
export const openGameStats = () => window.dispatchEvent(new Event("footygami:stats"));

const iconSx = { color: "#e6e8ee", width: 38, height: 38, "&:hover": { color: BRAND.accent, background: accentTint(0.08) } };

export default function GameBar() {
  const { pathname } = useLocation();
  const archive = pathname === "/daily/archive";
  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        background: "rgba(15,15,19,0.92)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        borderBottom: `1px solid ${BRAND.divider}`,
      }}
    >
      <Box sx={{ maxWidth: 980, mx: "auto", px: { xs: 1, sm: 2 }, py: 0.75, display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.75 }}>
        <Box component={RouterLink} to="/" aria-label="Footygami home" sx={{ display: "flex", alignItems: "center", textDecoration: "none", p: 0.5, borderRadius: 1.5 }}>
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <LogoMark size={26} />
          </Box>
          <Box sx={{ display: { xs: "none", md: "flex" } }}>
            <BrandLockup logoSize={24} wordSize={17} />
          </Box>
        </Box>

        {/* The games: in the bar from 600px, on their own row below it on phones. */}
        <Box
          component="nav"
          aria-label="Games"
          sx={{ order: { xs: 3, sm: 0 }, flexBasis: { xs: "100%", sm: "auto" }, flex: { sm: 1 }, display: "flex", justifyContent: "center", gap: 0.5 }}
        >
          {TABS.map((t) => {
            const on = pathname === t.to;
            return (
              <Box
                key={t.to}
                component={RouterLink}
                to={t.to}
                aria-current={on ? "page" : undefined}
                sx={{
                  flex: { xs: 1, sm: "0 0 auto" },
                  textAlign: "center",
                  px: { xs: 0.75, sm: 1.75 },
                  py: 0.75,
                  borderRadius: 999,
                  fontSize: { xs: 12.5, sm: 13.5 },
                  fontWeight: 800,
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                  color: on ? BRAND.onAccent : BRAND.textSecondary,
                  background: on ? BRAND.accent : "rgba(255,255,255,0.05)",
                  "&:hover": { color: on ? BRAND.onAccent : "#fff" },
                }}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  {t.label}
                </Box>
                <Box component="span" sx={{ display: { sm: "none" } }}>
                  {t.short}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.25 }}>
          {!archive && (
            <>
              <Tooltip title="How to play">
                <IconButton aria-label="How to play" onClick={openGameHelp} sx={iconSx}>
                  <InfoOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Your stats">
                <IconButton aria-label="Your stats" onClick={openGameStats} sx={iconSx}>
                  <BarChartRounded />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title="Past puzzles">
            <IconButton
              component={RouterLink}
              to="/daily/archive"
              aria-label="Past puzzles"
              aria-current={archive ? "page" : undefined}
              sx={{ ...iconSx, ...(archive ? { color: BRAND.accent, background: accentTint(0.12) } : {}) }}
            >
              <CalendarMonthRounded />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
}
